/**
 * statusApi.ts — typed wrapper for the public platform-status endpoint.
 *
 * The backend at /api/status normalizes BetterStack's status-page JSON into
 * a small, stable shape so the frontend never has to know about upstream
 * indicator vocabulary changes. apiClient.get already unwraps AxiosResponse,
 * so the return value IS the HTTP body.
 */

import { apiClient } from './apiClient';

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Normalized platform status. The string union is exhaustive on the backend
 * side (see `normalizeIndicator` in routes/status.ts) — anything the upstream
 * doesn't map to a known indicator falls through to `unknown`, which the UI
 * renders as a grey "status check unavailable" pill.
 */
export type PlatformStatusKind = 'operational' | 'degraded' | 'down' | 'maintenance' | 'unknown';

export interface PlatformStatus {
  status: PlatformStatusKind;
  /** Human-readable label (e.g. "All Systems Operational"). Surfaced verbatim
   *  in the badge — comes from BetterStack so it already speaks user-language. */
  description: string;
  /** ISO-8601 timestamp of the upstream's last update, when present. */
  updatedAt?: string;
  /** True when the API is serving its last-known-good Redis snapshot because
   *  upstream was unreachable. The badge dims slightly when this is set so
   *  ops can spot a stuck cache. */
  stale?: boolean;
}

interface StatusEnvelope {
  success: boolean;
  data: PlatformStatus;
}

// ── Calls ─────────────────────────────────────────────────────────────────────

export async function fetchPlatformStatus(): Promise<PlatformStatus> {
  const res = await apiClient.get<StatusEnvelope>('/status');
  return res.data;
}
